import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, ShoppingBag, ArrowLeft } from "lucide-react";
import userAPI from "../../api/UserApi";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { handleApiValidationError } from "../../utils/apiErrorHandler";

export default function ForgotPasswordCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  const INITIAL_FORM_DATA = {
    email: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [general, setGeneral] = useState("");
  const [loading, setLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);

  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!validate()) {
      setLoading(false);
      return;
    }

    try {
      const res = await userAPI.forgotPassword(formData);

      if (res.code === 200) {
        setGeneral(res.message);
        setFormData(INITIAL_FORM_DATA);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      {/* Left panel — decorative */}
      <div className="auth-panel auth-panel--left">
        <div className="auth-panel__overlay" />
        <div className="auth-panel__content">
          <div className="auth-brand">
            <ShoppingBag size={32} color="#C9991E" />
            <span>Premium Store</span>
          </div>
          <h2 className="auth-panel__headline">
            Lost access? Let's get you back in.
          </h2>
          <p className="auth-panel__sub">
            Enter the email linked to your account and we'll send you a secure link to reset your password.
          </p>
          <div className="auth-panel__dots">
            <span className="dot dot--active" />
            <span className="dot" />
            <span className="dot" />
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="auth-panel auth-panel--right">
        <div className="auth-form-box">
          <Link to="/users/login" className="auth-back-link">
            <ArrowLeft size={15} />
            Back to Login
          </Link>

          <p className="auth-form-eyebrow">Account Recovery</p>
          <h1 className="auth-form-title">Forgot Password</h1>
          <p className="auth-form-subtitle">
            No worries, enter your email to reset it
          </p>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            {/* Email */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="email">
                Email Address
              </label>
              <div className={`auth-input-wrap ${errors.email ? "has-error" : ""}`}>
                <Mail size={17} className="auth-input-icon" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <span className="auth-error">{errors.email}</span>
              )}
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  Send Reset Link <ArrowRight size={17} className="ms-2" />
                </>
              )}
            </button>

            {general && (
              <div className="auth-success-msg">{general}</div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}